/**
 * Shared behaviour for horizontal menu / tab strips (StudioHeadTabs, Settings
 * sections, …): wheel → sideways, optional edge fade, and the active item is
 * kept fully in view (respecting CSS `scroll-padding-inline`) so a selected
 * pill is never left clipped at the strip edge.
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import { bindHorizontalScrollFade } from "./use-horizontal-scroll-fade";
import { useHorizontalWheelScroll } from "./use-horizontal-wheel-scroll";

const ACTIVE_SELECTOR = '.is-active, [aria-selected="true"], [aria-current="page"], [aria-current="true"]';
const FADE_PX = 28;

const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Scroll `strip` (only — never page ancestors) so `item` sits inside its padded viewport. */
export function scrollItemIntoStrip(strip, item, { behavior = "smooth" } = {}) {
  if (!strip || !item) return;
  const max = strip.scrollWidth - strip.clientWidth;
  if (max <= 1) return;
  const cs = getComputedStyle(strip);
  const fade = strip.classList.contains("cursor-h-scroll-fade") ? FADE_PX : 0;
  const padStart = Math.max(parseFloat(cs.scrollPaddingInlineStart) || 0, fade, 8);
  const padEnd = Math.max(parseFloat(cs.scrollPaddingInlineEnd) || 0, fade, 8);
  const sr = strip.getBoundingClientRect();
  const ir = item.getBoundingClientRect();
  const left = ir.left - sr.left + strip.scrollLeft;
  const right = left + ir.width;
  let target = null;
  if (left - padStart < strip.scrollLeft) target = left - padStart;
  else if (right + padEnd > strip.scrollLeft + strip.clientWidth) {
    target = right + padEnd - strip.clientWidth;
  }
  if (target == null) return;
  const next = Math.min(max, Math.max(0, Math.round(target)));
  if (Math.abs(next - strip.scrollLeft) < 1) return;
  strip.scrollTo({ left: next, behavior: prefersReducedMotion() ? "auto" : behavior });
}

/**
 * @param {{ current: HTMLElement | null }} ref
 * @param {{ activeKey?: unknown, fade?: boolean, wheel?: boolean }} [options]
 */
export function useHorizontalMenuScroller(ref, options = {}) {
  const { activeKey, fade = false, wheel = true } = options;
  useHorizontalWheelScroll(ref, wheel);

  useEffect(() => {
    if (!fade) return undefined;
    const el = ref?.current;
    if (!el) return undefined;
    el.classList.add("cursor-h-scroll-fade");
    const unbind = bindHorizontalScrollFade(el);
    return () => {
      unbind();
      el.classList.remove("cursor-h-scroll-fade");
      delete el.dataset.scrollFade;
    };
  }, [ref, fade]);

  const firstRef = useRef(true);
  useIsoLayoutEffect(() => {
    const el = ref?.current;
    if (!el) return undefined;
    const behavior = firstRef.current ? "auto" : "smooth";
    firstRef.current = false;
    const run = () => {
      const active = el.querySelector(ACTIVE_SELECTOR);
      if (active) scrollItemIntoStrip(el, active, { behavior });
    };
    run();
    // Labels/fonts may settle a frame later (lazy sections, web fonts).
    const id = requestAnimationFrame(run);
    return () => cancelAnimationFrame(id);
  }, [ref, activeKey]);
}
